// src/components/admin/pagos/planEditor/PlanEditorWizard.js
import React, { useState, useEffect } from 'react'
import {
  FileText, ListChecks, CalendarClock, Package, ClipboardCheck,
  Check, ChevronLeft, ChevronRight, AlertTriangle,
} from 'lucide-react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  Button, Input, Label,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../ui'
import { formatMoney } from '../../../../utils/helpers'
import { validarParcialidades, totalConceptosGuardables, conceptosGuardables } from '../../../../lib/services/pagosPlanHelpers'
import {
  createPlan, updatePlan, reconcilePlanItems,
  reconcileParcialidades, getEntregables, addEntregable, removeEntregable,
  getPrendas,
} from '../../../../lib/supabase'
import { PLAN_SCOPES, scopeLabel } from '../../../../constants/planScope'
import { useConfirmDialog } from '../../../../hooks/useConfirmDialog'
import { useUnsavedGuard } from '../../../../hooks/useUnsavedGuard'
import ConceptosSection from './ConceptosSection'
import ParcialidadesSection from './ParcialidadesSection'
import { opcionesDeConceptoDelPlan } from './opcionesConceptoDelPlan'
import { agregarUniformeAlPlan, quitarUniformeDelPlan } from './uniformeEnPlan'
import EntregablesSection from './EntregablesSection'

const STEPS = [
  { id: 1, label: 'Datos', icon: FileText, hint: 'Nombre, alcance y descripción del plan.' },
  { id: 2, label: 'Conceptos', icon: ListChecks, hint: 'De qué se compone el costo. Su suma es el total.' },
  { id: 3, label: 'Parcialidades', icon: CalendarClock, hint: 'En cuántos pagos y fechas se cubre el total.' },
  { id: 4, label: 'Entregables', icon: Package, hint: 'Cosas que le das al jugador (uniforme, credencial) y cuándo puede recibirlas. Opcional.' },
  { id: 5, label: 'Revisar', icon: ClipboardCheck, hint: 'Confirma y guarda el plan.' },
]

const PlanEditorWizard = ({ open, onOpenChange, plan, temporadaId, temporadas = [], conceptosCatalogo = [], onSaved }) => {
  const { alert, ConfirmDialog } = useConfirmDialog()
  const guard = useUnsavedGuard({ onClose: () => onOpenChange(false) })
  const [step, setStep] = useState(1)
  const [form, setForm] = useState({ nombre: '', descripcion: '', scope: 'libre', temporada_id: '' })
  const [conceptos, setConceptos] = useState([])
  const [parcialidades, setParcialidades] = useState([])
  const [entregables, setEntregables] = useState([])
  const [saving, setSaving] = useState(false)
  const [prendaConceptoIds, setPrendaConceptoIds] = useState(new Set())
  const [prendas, setPrendas] = useState([])

  const conceptoOptions = conceptosCatalogo.map((c) => ({ value: c.id, label: c.nombre, montoDefault: c.monto_default }))

  // Un plan que ya emitio cargos no es un borrador: hay gente con un calendario
  // de pagos en la mano. Eso cambia dos cosas -- no se puede guardar descuadrado,
  // y si le mueves a los montos hay que avisar que los cargos quedaron atras.
  const cargosEmitidos = plan?.pagos_cargos?.[0]?.count || 0

  // Dos formas de dejar atras a los cargos ya emitidos: cambiarle el monto a un
  // pago, o agregar un pago que nadie tiene todavia. Las dos se corrigen en el
  // mismo lugar, asi que las dos abren el aviso al guardar.
  // Agregar un uniforme al plan: un clic. Le pone su concepto (el precio) y su
  // parcialidad propia (el cobro), asi que el plan sigue cuadrado —se ve en el acto
  // en el marcador de arriba— y despues se le puede preguntar "¿pago el jersey?".
  // La FECHA no se pide aqui: la pone el pedido.
  const prendasDisponibles = prendas.filter(
    (p) => !conceptos.some((c) => c.concepto_id === p.concepto_id),
  )

  const agregarUniforme = (prenda) => {
    if (!prenda) return
    const siguiente = agregarUniformeAlPlan({ conceptos, parcialidades, entregables }, prenda)
    setConceptos(siguiente.conceptos)
    setParcialidades(siguiente.parcialidades)
    setEntregables(siguiente.entregables)
    guard.markDirty()
  }

  const quitarUniforme = (conceptoId) => {
    const siguiente = quitarUniformeDelPlan({ conceptos, parcialidades, entregables }, conceptoId)
    setConceptos(siguiente.conceptos)
    setParcialidades(siguiente.parcialidades)
    setEntregables(siguiente.entregables)
    guard.markDirty()
  }

  const cambiaLoQueSeCobra = () => {
    const antes = plan?.pagos_planes_parcialidades || []
    if (!antes.length) return false
    const previo = {}
    antes.forEach((p) => { previo[p.id] = Number(p.monto || 0) })
    return parcialidades.some((p) => !p.id || Number(p.monto || 0) !== previo[p.id])
  }
  // Conceptos entregables = uniformes que están en el plan (se pueden elegir como entregable).
  const conceptosEntregables = conceptos
    .filter((c) => c.concepto_id && prendaConceptoIds.has(c.concepto_id))
    .map((c) => ({ value: c.concepto_id, label: conceptoOptions.find((o) => o.value === c.concepto_id)?.label || 'Uniforme' }))
  // Total = solo conceptos con concepto seleccionado (lo que realmente se guarda)
  const total = totalConceptosGuardables(conceptos)
  const v = validarParcialidades([{ monto: total }], parcialidades)

  // Estado de cuadre (resumen persistente)
  let cuadreLabel, cuadreClass
  if (parcialidades.length === 0) {
    cuadreLabel = 'Sin parcialidades'
    cuadreClass = 'bg-muted text-muted-foreground'
  } else if (v.cuadra) {
    cuadreLabel = 'Cuadra ✓'
    cuadreClass = 'bg-green-100 text-green-800'
  } else if (v.diferencia < 0) {
    cuadreLabel = `Faltan ${formatMoney(Math.abs(v.diferencia))}`
    cuadreClass = 'bg-amber-100 text-amber-800'
  } else {
    cuadreLabel = `Sobran ${formatMoney(v.diferencia)}`
    cuadreClass = 'bg-amber-100 text-amber-800'
  }

  // Validaciones de datos. Las "blocking" impiden guardar el plan; los "avisos" solo informan.
  const conceptosValidos = conceptos.filter((c) => c.concepto_id && Number(c.monto) > 0)
  const issues = []
  if (!form.nombre.trim()) issues.push({ step: 1, msg: 'Falta el nombre del plan.', blocking: true })
  if (conceptosValidos.length === 0) issues.push({ step: 2, msg: 'Agrega al menos un concepto con monto mayor a $0.', blocking: true })
  if (conceptos.some((c) => c.concepto_id && !(Number(c.monto) > 0))) issues.push({ step: 2, msg: 'Hay conceptos sin monto.', blocking: false })
  if (conceptos.some((c) => !c.concepto_id && Number(c.monto) > 0)) issues.push({ step: 2, msg: 'Hay un concepto con monto pero sin seleccionar; no se guardará ni cuenta en el total.', blocking: false })
  {
    const ids = conceptosValidos.map((c) => c.concepto_id)
    if (new Set(ids).size !== ids.length) issues.push({ step: 2, msg: 'Hay conceptos repetidos: se guardarán sumados en una sola línea (el total no cambia).', blocking: false })
  }
  if (parcialidades.length === 0) {
    issues.push({ step: 3, msg: 'Agrega al menos una parcialidad.', blocking: true })
  } else {
    if (!v.cuadra) issues.push({ step: 3, msg: `Las parcialidades no suman el total (${cuadreLabel.toLowerCase()}).`, blocking: true })
    if (parcialidades.some((p) => !(Number(p.monto) > 0))) issues.push({ step: 3, msg: 'Hay parcialidades sin monto.', blocking: false })
    // El pago de un uniforme nace SIN fecha a proposito: se la pone el pedido, porque
    // un pedido no es de todos. Avisar de esas seria avisar de algo que esta bien.
    const esUniforme = (p) => p.concepto_id && prendaConceptoIds.has(p.concepto_id)
    if (parcialidades.some((p) => !p.fecha_limite && !esUniforme(p))) issues.push({ step: 3, msg: 'Hay parcialidades sin fecha límite.', blocking: false })
    if (parcialidades.some((p) => !p.fecha_limite && esUniforme(p))) {
      issues.push({ step: 3, msg: 'Los uniformes no llevan fecha aquí: se la pone el pedido cuando los mandes a hacer.', blocking: false })
    }
  }
  // Un pago nuevo no le llega solo a quien ya esta asignado: hay que emitirlo.
  // Al guardar se ofrece hacerlo, pero conviene saberlo antes de guardar.
  if (cargosEmitidos > 0 && parcialidades.some((p) => !p.id)) {
    issues.push({
      step: 3,
      msg: 'Agregaste un pago a un plan que ya cobra: al guardar te preguntaré si lo emito para quienes ya lo tienen asignado.',
      blocking: false,
    })
  }
  entregables.forEach((e) => {
    if (!e.nombre?.trim() && !e.concepto_id) return
    // La regla 'concepto' no lleva pago: se libera al pagar esa misma prenda.
    if (e.regla_tipo !== 'concepto' && !e.hasta_orden) issues.push({ step: 4, msg: `"${e.nombre || 'Entregable'}": falta indicar hasta qué pago se entrega.`, blocking: false })
  })
  const blockingIssues = issues.filter((i) => i.blocking)
  const avisos = issues.filter((i) => !i.blocking)
  const puedeGuardar = blockingIssues.length === 0
  const stepTieneBloqueo = (id) => blockingIssues.some((i) => i.step === id)

  // Cargar datos al abrir / cambiar de plan
  useEffect(() => {
    if (!open) return
    setStep(1)
    if (plan) {
      setForm({ nombre: plan.nombre || '', descripcion: plan.descripcion || '', scope: plan.scope || 'libre', temporada_id: plan.temporada_id || temporadaId || '' })
      // Una linea por concepto desde que abre. Los planes viejos traen el mismo
      // concepto repetido (4 lineas de "Mensualidad"); mostrarlas sueltas contradice
      // la propia regla del editor -- el desplegable esconde los conceptos ya usados --
      // y ademas al guardar se sumaban igual. Mejor verlas ya sumadas.
      setConceptos(conceptosGuardables((plan.pagos_planes_items || []).map((it) => ({ concepto_id: it.concepto_id, monto: it.monto }))))
      setParcialidades((plan.pagos_planes_parcialidades || []).slice().sort((a, b) => a.orden - b.orden))
      setEntregables(plan.pagos_planes_entregables || [])
    } else {
      setForm({ nombre: '', descripcion: '', scope: 'libre', temporada_id: temporadaId || '' })
      setConceptos([])
      setParcialidades([])
      setEntregables([])
    }
  }, [open, plan, temporadaId])

  // Conceptos que pertenecen a una prenda (no se eligen aquí, se gestionan en Uniformes)
  useEffect(() => {
    if (!open) return
    let cancelled = false
    getPrendas().then(({ data }) => {
      if (cancelled) return
      const conPrecio = (data || []).filter((p) => p.concepto_id)
      setPrendas(conPrecio)
      setPrendaConceptoIds(new Set(conPrecio.map((p) => p.concepto_id)))
    })
    return () => { cancelled = true }
  }, [open])

  const next = () => setStep((s) => Math.min(STEPS.length, s + 1))
  const prev = () => setStep((s) => Math.max(1, s - 1))

  const persistir = async () => {
    if (!form.nombre.trim()) { await alert('Falta nombre', 'El plan necesita un nombre'); setStep(1); return }
    setSaving(true)
    try {
      let planId = plan?.id
      if (planId) {
        await updatePlan(planId, { nombre: form.nombre, descripcion: form.descripcion || null, scope: form.scope, temporada_id: form.temporada_id || temporadaId })
      } else {
        const { data } = await createPlan({ ...form, temporada_id: form.temporada_id || temporadaId })
        planId = data?.id
      }
      if (!planId) throw new Error('No se pudo crear el plan')

      // Los items (líneas de concepto) NO tienen FK de cargos, así que se regeneran
      // para reflejar EXACTAMENTE los conceptos del editor (únicos), incluidas las
      // prendas. Así el total persistido == el total que el editor valida ("Cuadra ✓"),
      // y de paso se limpian conceptos duplicados.
      //
      // Las parcialidades de prenda YA NO se excluyen: al retirarse la pantalla
      // "Prendas por plan", este editor es el unico que las gestiona. El estado local
      // las carga todas, asi que reconciliar no borra nada.
      // Los renglones se RECONCILIAN, no se borran y recrean: los cargos ya
      // emitidos cuelgan de ellos por FK. Si algo falla se aborta aqui, antes de
      // tocar nada mas -- guardar a medias fue lo que duplico conceptos.
      const { error: itemsErr } = await reconcilePlanItems(planId, conceptosGuardables(conceptos).map((c) => ({
        concepto_id: c.concepto_id, monto: Number(c.monto || 0),
      })))
      if (itemsErr) throw itemsErr

      const { error: parcErr } = await reconcileParcialidades(planId, parcialidades.map((p, i) => ({
        id: p.id, orden: i + 1, nombre: p.nombre, monto: Number(p.monto || 0), fecha_limite: p.fecha_limite || null, concepto_id: p.concepto_id || null,
      })))
      if (parcErr) { await alert('No se pudo guardar', parcErr.message || 'Error al guardar las parcialidades'); setSaving(false); return }

      // Los entregables se regeneran por completo desde el editor (ya no hay
      // entregables "automáticos" de prenda: el usuario los define aquí).
      const { data: prevEntregables } = await getEntregables(planId)
      for (const e of (prevEntregables || [])) { await removeEntregable(e.id) }
      for (const e of entregables) {
        if (!e.nombre?.trim() && !e.concepto_id) continue
        // La regla la manda el entregable: 'concepto' = se libera al pagar ESA prenda
        // (la de un uniforme del plan); 'parcialidad' = al llegar al Pago N.
        await addEntregable(planId, { ...e, regla_tipo: e.regla_tipo || 'parcialidad' })
      }

      setSaving(false)
      guard.resetDirty()
      onOpenChange(false)
      onSaved?.(planId, { recalcular: cargosEmitidos > 0 && cambiaLoQueSeCobra() })
    } catch (err) {
      setSaving(false)
      await alert('Error', err.message || 'No se pudo guardar')
    }
  }

  const activeStep = STEPS.find((s) => s.id === step)

  return (
    <Dialog open={open} onOpenChange={guard.onOpenChange}>
      <DialogContent {...guard.dirtyProps} className="flex flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
        {/* Encabezado + stepper + resumen persistente */}
        <DialogHeader className="space-y-3 border-b px-4 pb-3 pt-4 text-left sm:px-6">
          <DialogTitle className="pr-8">{plan ? 'Editar plan de pago' : 'Nuevo plan de pago'}</DialogTitle>

          {/* Stepper (clickeable) */}
          <div className="flex items-center">
            {STEPS.map((s, i) => {
              const Icon = s.icon
              const isActive = step === s.id
              const isDone = step > s.id
              return (
                <React.Fragment key={s.id}>
                  <button
                    type="button"
                    onClick={() => setStep(s.id)}
                    className="flex shrink-0 items-center gap-2"
                    title={s.label}
                  >
                    <span className="relative">
                      <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold transition-colors ${
                        isDone ? 'bg-green-600 text-white' : isActive ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                      }`}>
                        {isDone ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                      </span>
                      {stepTieneBloqueo(s.id) && !isActive && (
                        <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-background bg-amber-500" />
                      )}
                    </span>
                    <span className={`hidden text-xs font-medium sm:inline ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {s.label}
                    </span>
                  </button>
                  {i < STEPS.length - 1 && (
                    <div className={`mx-1.5 h-0.5 flex-1 sm:mx-2 ${step > s.id ? 'bg-green-600' : 'bg-muted'}`} />
                  )}
                </React.Fragment>
              )
            })}
          </div>

          {/* Resumen siempre visible */}
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <span className="text-xs text-muted-foreground">Total del plan </span>
              <span className="text-base font-bold">{formatMoney(total)}</span>
            </div>
            <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${cuadreClass}`}>{cuadreLabel}</span>
          </div>
        </DialogHeader>

        {/* Cuerpo del paso */}
        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          <div className="mb-3">
            <h3 className="text-sm font-semibold sm:hidden">{activeStep?.label}</h3>
            <p className="text-xs text-muted-foreground">{activeStep?.hint}</p>
          </div>

          {step === 1 && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Nombre *</Label>
                <Input value={form.nombre} onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))} placeholder="Ej: Temporada Ago-Feb 2026" />
              </div>
              {/* La temporada define a que ciclo pertenece todo lo que este plan cobre.
                  Antes era invisible aqui -- se heredaba del selector de la pantalla, que
                  queda tapado por este modal -- y no habia forma de corregirla despues:
                  un plan nacido en la temporada equivocada solo se podia borrar y rehacer.
                  Se congela en cuanto emite cargos, porque cada cargo lleva la suya. */}
              <div className="space-y-1.5">
                <Label>Temporada</Label>
                <Select
                  value={form.temporada_id}
                  onValueChange={(val) => { guard.markDirty(); setForm((p) => ({ ...p, temporada_id: val })) }}
                  disabled={cargosEmitidos > 0}
                >
                  <SelectTrigger><SelectValue placeholder="Seleccionar temporada" /></SelectTrigger>
                  <SelectContent>
                    {temporadas.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-muted-foreground">
                  {cargosEmitidos > 0
                    ? 'No se puede cambiar: el plan ya generó cargos, y cada cargo pertenece a esta temporada.'
                    : 'A qué temporada pertenece este plan. Puedes cambiarla mientras no haya generado cargos.'}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Alcance</Label>
                <Select value={form.scope} onValueChange={(val) => { guard.markDirty(); setForm((p) => ({ ...p, scope: val })) }}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar alcance" /></SelectTrigger>
                  <SelectContent>
                    {PLAN_SCOPES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-muted-foreground">
                  {PLAN_SCOPES.find((s) => s.value === form.scope)?.ayuda}
                  {' '}A quién se le aplica se elige después, en Asignaciones.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Descripción</Label>
                <Input value={form.descripcion} onChange={(e) => setForm((p) => ({ ...p, descripcion: e.target.value }))} placeholder="Opcional" />
              </div>
            </div>
          )}

          {step === 2 && (
            <ConceptosSection
              conceptos={conceptos}
              conceptoOptions={conceptoOptions}
              prendaConceptoIds={prendaConceptoIds}
              prendasDisponibles={prendasDisponibles}
              onAgregarUniforme={agregarUniforme}
              onQuitarUniforme={quitarUniforme}
              onChange={setConceptos}
            />
          )}

          {/* Parcialidades recibe solo los conceptos DE ESTE PLAN: vincular un pago
              a algo que el plan no incluye deja el cobro apuntando al vacio. Antes
              se pasaba el catalogo entero y en el plan de FLAG salian prendas de
              tackle. */}
          {step === 3 && (
            <ParcialidadesSection parcialidades={parcialidades} total={total} conceptoOptions={opcionesDeConceptoDelPlan(conceptos, conceptoOptions)} onChange={setParcialidades} />
          )}

          {step === 4 && (
            <EntregablesSection entregables={entregables} parcialidades={parcialidades} conceptosEntregables={conceptosEntregables} onChange={setEntregables} />
          )}

          {step === 5 && (
            <div className="space-y-3">
              <div className="divide-y rounded-lg border text-sm">
                {[
                  ['Nombre', form.nombre || <span className="text-muted-foreground">Sin nombre</span>],
                  ['Alcance', scopeLabel(form.scope)],
                  ['Total del plan', formatMoney(total)],
                  ['Conceptos', `${conceptos.filter((c) => c.concepto_id).length}`],
                  ['Parcialidades', `${parcialidades.length}`],
                  ['Entregables', `${entregables.filter((e) => e.nombre?.trim()).length}`],
                ].map(([k, val]) => (
                  <div key={k} className="flex items-center justify-between gap-3 px-3 py-2">
                    <span className="text-muted-foreground">{k}</span>
                    <span className="text-right font-medium">{val}</span>
                  </div>
                ))}
              </div>

              {blockingIssues.length === 0 && avisos.length === 0 && (
                <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                  <Check className="h-4 w-4 shrink-0" /> Todo listo. Puedes guardar y asignar el plan.
                </div>
              )}
              {blockingIssues.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  <p className="mb-1.5 flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4 shrink-0" /> Falta para poder guardar y asignar:</p>
                  <ul className="space-y-1">
                    {blockingIssues.map((it, idx) => (
                      <li key={idx} className="flex items-start justify-between gap-2">
                        <span>• {it.msg}</span>
                        <Button variant="link" className="h-auto shrink-0 p-0 text-amber-900 underline" onClick={() => setStep(it.step)}>Ir</Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {avisos.length > 0 && (
                <div className="rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground">
                  <p className="mb-1.5 font-medium text-foreground">Avisos (no impiden guardar y asignar):</p>
                  <ul className="space-y-1">
                    {avisos.map((it, idx) => (
                      <li key={idx} className="flex items-start justify-between gap-2">
                        <span>• {it.msg}</span>
                        <Button variant="link" className="h-auto shrink-0 p-0 underline" onClick={() => setStep(it.step)}>Ir</Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer de navegación */}
        <div className="flex items-center justify-between gap-2 border-t px-4 py-3 sm:px-6">
          <Button variant="outline" onClick={() => (step === 1 ? guard.onOpenChange(false) : prev())} disabled={saving}>
            {step === 1 ? 'Cancelar' : <><ChevronLeft className="mr-1 h-4 w-4" /> Atrás</>}
          </Button>

          <div className="flex items-center gap-2">
            {/* Atajo para dejar un plan a medias. Se retira en cuanto el plan ya
                le cobra a alguien: ahi guardar sin cuadrar dejaria un calendario
                de pagos que no suma el total, y con gente dentro. */}
            {cargosEmitidos === 0 && (
              <Button variant="ghost" className="px-2 sm:px-4" onClick={() => persistir()} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar sin terminar'}
              </Button>
            )}
            {step < STEPS.length ? (
              <Button onClick={next}>Siguiente <ChevronRight className="ml-1 h-4 w-4" /></Button>
            ) : (
              // Sin confirmacion: guardar un plan ya no genera cargos -- solo
              // define QUE se cobra. A quien se le cobra se elige aparte, en
              // Pagos -> Asignaciones. Preguntar aqui seria friccion sin motivo.
              <Button onClick={() => persistir()} disabled={saving || !puedeGuardar}>
                {saving ? 'Guardando...' : 'Guardar plan'}
              </Button>
            )}
          </div>
        </div>
        {guard.DiscardDialog}
      </DialogContent>
      <ConfirmDialog />
    </Dialog>
  )
}

export default PlanEditorWizard
